import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { subDays } from 'date-fns';
import { Typography } from '@components/Typography';
import Button from '@components/Button';
import { Checkbox } from '@components/Checkbox';
import { InternalLink } from '@components/Link';
import { ProfileName } from '@constants/integrations';
import { PLACEHOLDER_CONTEXT } from '@constants/placeholders';
import { RoutePath } from '@constants/routes';
import { AITaskType, AI_TASK_TYPE } from '@constants/ai-assistant';
import { reviewsService } from '@services';
import { alertActions, reviewActions } from '@actions';
import { formatDateOrEmpty } from '@helpers/dates';
import { handleApiError } from '@helpers/api';
import { findUnresolvedPlaceholders, isRecoveryFormToken, resolveRecoveryFormToken } from '@helpers/recoveryForm';
import { parseTipTapJSONToPlainText } from '@helpers/text-editor';
import { useAccountPeople, useTranslation, useThrottle, useCopyToClipboard, usePermissions } from '@hooks';
import { ReplyIcon, ReplyArrowIcon } from 'assets/images/icons';
import RespondTemplate from '../RespondTemplate';
import RecoveryFormButton from './RecoveryFormButton';
import ReplyAuthorAvatar from './ReplyAuthorAvatar';
import {
    ReplyIconWrapper,
    ReplyAuthorWrapper,
    ReplyContainer,
    ReplyContent,
    ReplyBtnWrapper,
    FormWrapper,
    ReplyEditor,
    CheckboxWrapper,
} from './styles';
import { ProductEvent } from '@constants/product-events';
import PostHogManager from '@widgets/posthog-manager';

const IFOOD_EMPTY_FEEDBACK_MARKER = 'Feedback em branco';
const IFOOD_REPLY_WINDOW_DAYS = 5;
const IFOOD_NON_REPLYABLE_RATING = 5;

const UNCLAIMED_LOCATION_ERROR = 'google_reply_unclaimed_location';

const REPLY_MODE = {
    EMPTY: 'empty',
    FORM: 'form',
};

const ACTION = {
    REPLY: 0,
    MARK_AS_REPLIED: 1,
    COPY_AND_OPEN: 2,
};

const REPLY_ACTION_NAME = {
    [ACTION.REPLY]: 'reply',
    [ACTION.MARK_AS_REPLIED]: 'mark_as_replied',
    [ACTION.COPY_AND_OPEN]: 'copy_and_open',
};

const isUnverifiedGoogle = review =>
    review.integrationName === ProfileName.GoogleOfficial && review.isProfileVerified === false;

const UnverifiedListingNotice = () => {
    const { t } = useTranslation();
    const { hasAccess } = usePermissions();
    return (
        <Typography variant="body2" sx={{ marginBottom: '8px' }}>
            {t('ReviewsManage.unverifiedListingNotice')}
            {hasAccess('locations') && (
                <>
                    {' '}
                    <InternalLink to={RoutePath.LocationsProfiles}>
                        {t('ReviewsManage.verifyListingAction')}
                    </InternalLink>
                </>
            )}
        </Typography>
    );
};

const Reply = ({ language, review, isMobile }) => {
    const { t } = useTranslation();
    const { getPerson, getPersonName } = useAccountPeople();

    const [mode, setMode] = useState(REPLY_MODE.EMPTY);
    const [comment, setComment] = useState('');
    const [markAsReplied, setMarkAsReplied] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const reviews = useSelector(state => state.reviews.reviews || []);
    const dispatch = useDispatch();
    const { onCopy } = useCopyToClipboard();
    const captureReply = type =>
        PostHogManager.capture(ProductEvent.ReviewReplied, {
            action: REPLY_ACTION_NAME[type],
            edited: Boolean(review.ownerAnswer),
        });

    const [onCopyClick] = useThrottle(async value => {
        if (await onCopy(value)) {
            captureReply(ACTION.COPY_AND_OPEN);
        }
    }, 1000);

    const onClickReply = () => setMode(REPLY_MODE.FORM);

    const onEditReply = () => {
        setComment(review.ownerAnswer);
        setMode(REPLY_MODE.FORM);
    };

    const onDeleteReply = async () => {
        setIsSubmitting(true);
        try {
            const updatedReview = await reviewsService.deleteReply(review.reviewId);
            const reviewsCopy = reviews.slice();
            const reviewIndex = reviewsCopy.findIndex(r => r.reviewId === review.reviewId);
            setMode(REPLY_MODE.EMPTY);
            setComment('');
            if (reviewIndex !== -1 && updatedReview) {
                reviewsCopy[reviewIndex] = updatedReview?.webReview;
                dispatch(reviewActions.updateReviews(reviewsCopy));
            }
            if (isUnverifiedGoogle(review)) {
                dispatch(reviewActions.setListingVerification(review, true));
            }
        } catch (error) {
            if (error?.errorCode === UNCLAIMED_LOCATION_ERROR) {
                dispatch(reviewActions.setListingVerification(review, false));
            }
            handleApiError(error, 'alertMessages.deleteFail');
        }
        setIsSubmitting(false);
    };

    const onCancelReply = () => {
        setComment('');
        setMode(REPLY_MODE.EMPTY);
    };

    const onClickPublish = async type => {
        try {
            const resolvedComment = resolveRecoveryFormToken(comment, review.recoveryFormUrl);
            const unresolved = type === ACTION.MARK_AS_REPLIED ? [] : findUnresolvedPlaceholders(resolvedComment);
            if (unresolved.length) {
                dispatch(
                    alertActions.error(
                        unresolved.some(isRecoveryFormToken)
                            ? t('apiErrors.recovery_form_unresolved')
                            : t('ReviewsManage.unresolvedPlaceholders', { placeholders: unresolved.join(', ') }),
                    ),
                );
                return;
            }
            if (type === ACTION.COPY_AND_OPEN) {
                onCopyClick(resolvedComment);
                window.open(review.manageLink || review.link, '_blank');
                if (!markAsReplied) return;
            }
            if (review.integrationName === ProfileName.iFood) {
                if (resolvedComment.length < 10 || resolvedComment.length > 300) {
                    dispatch(alertActions.error(t('apiErrors.IFOOD_INVALID_REVIEW_REPLY_LENGTH')));
                    return;
                }
            }
            setIsSubmitting(true);
            let updatedReview = null;
            updatedReview = await reviewsService.replyReview(
                { comment: type === ACTION.MARK_AS_REPLIED ? '' : comment },
                review.reviewId,
            );
            if (type !== ACTION.COPY_AND_OPEN) {
                captureReply(type);
            }
            const reviewsCopy = reviews.slice();
            const reviewIndex = reviewsCopy.findIndex(r => r.reviewId === review.reviewId);
            setMode(REPLY_MODE.EMPTY);
            setComment('');
            if (reviewIndex !== -1 && updatedReview) {
                reviewsCopy[reviewIndex] = updatedReview.webReview;
                dispatch(reviewActions.updateReviews(reviewsCopy));
            }
            if (isUnverifiedGoogle(review)) {
                dispatch(reviewActions.setListingVerification(review, true));
            }
        } catch (error) {
            if (error?.errorCode === UNCLAIMED_LOCATION_ERROR) {
                dispatch(reviewActions.setListingVerification(review, false));
            }
            handleApiError(error);
        }
        setIsSubmitting(false);
    };

    const answerDate = formatDateOrEmpty(review.ownerAnswerDate, 'd MMM yyyy', language);

    const isOutsideIFoodReplyWindow = new Date(review.creationDate) < subDays(new Date(), IFOOD_REPLY_WINDOW_DAYS);

    const IS_NOT_EXTERNAL = [ProfileName.GoogleOfficial, ProfileName.iFood].includes(review.integrationName);
    const IS_GOOGLE = review.integrationName === ProfileName.GoogleOfficial;
    const IS_BOOKING = review.integrationName === ProfileName['Booking.com'];
    const IS_IFOOD = review.integrationName === ProfileName.iFood;
    const IS_UNVERIFIED_GOOGLE = isUnverifiedGoogle(review);
    const HAS_TEXT = Boolean(review.text?.trim());

    const replyAuthorName = getPersonName(review.repliedByUserId) || review.repliedByUserFullName;
    const hasReplyAuthor = !!replyAuthorName;
    const replyAction = review.manuallyReplied ? 'manuallyReplied' : 'replied';
    const captionKey = `${replyAction}${answerDate ? 'On' : ''}${hasReplyAuthor ? 'ByUser' : ''}`;

    return (
        <>
            {mode === REPLY_MODE.FORM && (
                <ReplyContainer>
                    <ReplyIconWrapper isForm>
                        <ReplyIcon />
                    </ReplyIconWrapper>
                    <ReplyContent>
                        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    color: 'var(--text-secondary)',
                                    '@media (max-width: 1024px)': {
                                        display: 'none !important',
                                    },
                                }}
                            >
                                {t(`ReviewsManage.${IS_NOT_EXTERNAL ? 'replyingAsOwner' : 'replyingOnExternal'}`)}
                            </Typography>
                            {!isMobile && (
                                <RespondTemplate
                                    setComment={setComment}
                                    comment={comment}
                                    review={review}
                                    isMobile={isMobile}
                                />
                            )}
                        </div>
                        {IS_UNVERIFIED_GOOGLE && <UnverifiedListingNotice />}
                        <FormWrapper>
                            {isMobile && <RespondTemplate setComment={setComment} comment={comment} review={review} />}
                            <ReplyEditor
                                placeholderContext={PLACEHOLDER_CONTEXT.REVIEW_REPLY}
                                initialValue={comment}
                                onChange={value => setComment(parseTipTapJSONToPlainText(value))}
                                label={t('ReviewsManage.yourReply')}
                                aiAssistant={{
                                    additionalOptions: [
                                        {
                                            text: t('AIAssistant.writeReply'),
                                            type: AITaskType.GenerateReplyToReview,
                                            taskType: AI_TASK_TYPE.CONTEXTUAL,
                                            reviewId: review.reviewId,
                                        },
                                    ],
                                }}
                                toolbar={review.recoveryFormUrl ? <RecoveryFormButton /> : null}
                            />
                            <ReplyBtnWrapper isExternal={!IS_NOT_EXTERNAL}>
                                {!IS_NOT_EXTERNAL ? (
                                    <CheckboxWrapper>
                                        <Checkbox
                                            inputProps={{ 'aria-label': 'controlled' }}
                                            onChange={() => setMarkAsReplied(!markAsReplied)}
                                            checked={markAsReplied}
                                        />
                                        <Typography variant="body2">{t('ReviewsManage.markAsReplied')}</Typography>
                                    </CheckboxWrapper>
                                ) : null}
                                {IS_NOT_EXTERNAL ? (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        disabled={!comment || isSubmitting}
                                        onClick={() => onClickPublish(ACTION.REPLY)}
                                    >
                                        {t('buttons.publish')}
                                    </Button>
                                ) : (
                                    <Button
                                        type="button"
                                        variant="contained"
                                        onClick={() => onClickPublish(ACTION.COPY_AND_OPEN)}
                                    >
                                        {t('buttons.copyAndOpen')}
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="outlined"
                                    sx={{ marginLeft: '4px' }}
                                    onClick={onCancelReply}
                                >
                                    {t('buttons.cancel')}
                                </Button>
                            </ReplyBtnWrapper>
                        </FormWrapper>
                    </ReplyContent>
                </ReplyContainer>
            )}
            {mode === REPLY_MODE.EMPTY && !review.isDeleted && review.ownerAnswer === null && (
                <ReplyContainer>
                    {IS_UNVERIFIED_GOOGLE && <UnverifiedListingNotice />}
                    <div style={{ display: 'flex', gap: '10px' }}>
                        {(IS_IFOOD &&
                            (!HAS_TEXT ||
                                review.text.includes(IFOOD_EMPTY_FEEDBACK_MARKER) ||
                                isOutsideIFoodReplyWindow ||
                                review.rating === IFOOD_NON_REPLYABLE_RATING)) ||
                        (IS_BOOKING && !HAS_TEXT) ? null : (
                            <Button
                                type="button"
                                variant="contained"
                                sx={{
                                    width: 'fit-content',
                                    '& svg path': {
                                        fill: '#fff',
                                    },
                                }}
                                onClick={onClickReply}
                                startIcon={<ReplyArrowIcon />}
                            >
                                {t('buttons.reply')}
                            </Button>
                        )}
                        {!IS_NOT_EXTERNAL && (
                            <Button
                                type="button"
                                variant="outlined"
                                sx={{ width: 'fit-content' }}
                                onClick={() => onClickPublish(ACTION.MARK_AS_REPLIED)}
                                disabled={isSubmitting}
                            >
                                {t('buttons.markAsReplied')}
                            </Button>
                        )}
                    </div>
                </ReplyContainer>
            )}
            {mode === REPLY_MODE.EMPTY && !review.isDeleted && review.ownerAnswer !== null && (
                <ReplyContainer>
                    <ReplyAuthorWrapper>
                        <ReplyAuthorAvatar
                            isAutoReply={review.isAutoReply}
                            repliedByUserId={review.repliedByUserId}
                            fullName={replyAuthorName}
                            photoUrl={review.repliedByUserPhotoUrl}
                            isRemoved={getPerson(review.repliedByUserId)?.isRemoved}
                        />
                    </ReplyAuthorWrapper>
                    <ReplyContent>
                        <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', marginBottom: '4px' }}>
                            {t(`ReviewsManage.${captionKey}`, {
                                name: replyAuthorName,
                                date: answerDate,
                            })}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {review.ownerAnswer || (IS_NOT_EXTERNAL ? t('ReviewsManage.noTextMsg') : '')}
                        </Typography>
                        {IS_UNVERIFIED_GOOGLE && <UnverifiedListingNotice />}
                        {(IS_GOOGLE || review.manuallyReplied) && (
                            <ReplyBtnWrapper hasEmptyReply={!review.ownerAnswer}>
                                <Button type="button" variant="outlined" onClick={onEditReply} disabled={isSubmitting}>
                                    {t('buttons.edit')}
                                </Button>
                                <Button
                                    type="button"
                                    variant="danger"
                                    sx={{ marginLeft: '10px' }}
                                    onClick={onDeleteReply}
                                    disabled={isSubmitting}
                                >
                                    {t('buttons.delete')}
                                </Button>
                            </ReplyBtnWrapper>
                        )}
                    </ReplyContent>
                </ReplyContainer>
            )}
        </>
    );
};

export default Reply;
